import {
  ListItemProps,
  MultiSelect,
  MultiSelectFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { cloneElement, useRef, useState } from "react";
import { ListItem } from "../form";

interface ICheckboxMultiSelectProps {
  id?: string;
  data: ListItem[];
  name?: string;
  value?: (number | string)[];
  disabled?: boolean;
  onChange?: (event: any, values: (number | string)[]) => void;
}

interface ISelectionsState {
  value: ListItem[];
  allSelected: boolean;
}

const SELECT_ALL = "Select All";
const DELAY = 300;

export const CheckboxMultiSelect = ({
  id,
  data,
  name,
  value,
  disabled,
  onChange,
}: ICheckboxMultiSelectProps) => {
  const listItems = [{ label: SELECT_ALL, value: SELECT_ALL }, ...data];
  const initialValues = listItems.filter((i) => value?.includes(i.value));

  const [dataState, setDataState] = useState({ data: listItems.slice(), loading: false });
  const [selections, setSelections] = useState<ISelectionsState>({
    value: initialValues,
    allSelected: initialValues.length === data.length,
  });

  const timeout = useRef<any>();

  const handleFilterChange = (event: MultiSelectFilterChangeEvent) => {
    const { filter } = event;

    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setDataState({
        data: filterBy(filter.value ? data.slice() : listItems.slice(), filter),
        loading: false,
      });
    }, DELAY);

    setDataState({
      ...dataState,
      loading: true,
    });
  };

  const handleChange = (event: any) => {
    let currentStateValue = [...selections.value];
    const values = event.target.value;
    let newValues: ISelectionsState = { value: [], allSelected: false };

    const difference =
      values.length > currentStateValue.length
        ? values.filter((v: ListItem) => !currentStateValue.includes(v))
        : currentStateValue.filter((v: ListItem) => !values.includes(v));

    if (difference[0].value === SELECT_ALL) {
      if (!selections.allSelected) newValues = { value: listItems, allSelected: true };
    } else {
      const items = values.filter((item: ListItem) => item.value !== SELECT_ALL);
      const isAll = items.length === data.length;
      newValues = { value: isAll ? listItems : items, allSelected: isAll };
    }

    setSelections(newValues);

    if (onChange)
      onChange(
        event,
        newValues.value.map((s) => s.value).filter((s) => s !== SELECT_ALL),
      );
  };

  const itemRender = (li: any, itemProps: ListItemProps) => {
    const itemChildren = (
      <span>
        <input
          className="k-checkbox k-checkbox-md k-rounded-md"
          type="checkbox"
          name={itemProps.dataItem.value}
          checked={itemProps.selected}
          onChange={(e: any) => itemProps.onClick(itemProps.index, e)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return cloneElement(li, li.props, itemChildren);
  };

  const length = selections.value.length;
  const selected = selections.allSelected ? length - 1 : length;

  return (
    <div style={{ minHeight: 250 }}>
      <MultiSelect
        id={id}
        name={name}
        data={dataState.data}
        loading={dataState.loading}
        disabled={disabled}
        textField="label"
        dataItemKey="value"
        itemRender={itemRender}
        value={selections.value}
        adaptive={true}
        autoClose={false}
        opened={true}
        filterable={true}
        onFilterChange={handleFilterChange}
        onChange={handleChange}
        popupSettings={{ width: "auto" }}
        tags={
          selected > 0 ? [{ text: `${selected} items selected`, data: [...selections.value] }] : []
        }
      />
    </div>
  );
};
